'use client';

import { JSX, MouseEvent } from 'react';
import Link from 'next/link';
import { trackAdobeDownload, getDocumentNameFromUrl } from '@/utils/adobe';

// Reusable download link that reports an Adobe Download event on click.
// Wraps next/link so it serves both external file URLs and internal proxy routes,
// keeping the client-only tracking isolated from the server components that render it.
type DownloadLinkProps = React.ComponentProps<typeof Link> & {
  // Human-readable label for the Download event (drives eventDesc)
  clickName: string;
  // File name reported to analytics; derived from the href when omitted
  documentName?: string;
  // Adobe Click type for the auto-fired Click event; auto-derived from the anchor when omitted
  clickType?: string;
  // When true, fetches the file as a blob and saves it to disk instead of opening it in a tab.
  // Needed for cross-origin files, where the native anchor `download` attribute is ignored.
  forceDownload?: boolean;
  // Saved file name used in forceDownload mode; falls back to documentName, then 'download'
  downloadFileName?: string;
};

// Fetches a file as a blob and triggers a browser save; falls back to opening the URL on failure
const saveFileToDisk = async (url: string, fileName: string): Promise<void> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(objectUrl);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

export const DownloadLink = ({
  href,
  clickName,
  documentName,
  clickType,
  forceDownload = false,
  downloadFileName,
  children,
  ...rest
}: DownloadLinkProps): JSX.Element => {
  // Fire the Download event before navigation; derive the file name from the URL when not provided
  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    const url = typeof href === 'string' ? href : '';
    // Pass the event so the auto-fired Click event can derive its location/type from the anchor
    trackAdobeDownload(
      { clickName, documentName: documentName || getDocumentNameFromUrl(url), clickType },
      e
    );

    // Save cross-origin files to disk instead of letting the browser open them in a new tab
    if (forceDownload && url) {
      e.preventDefault();
      void saveFileToDisk(url, downloadFileName || documentName || 'download');
    }
  };

  return (
    <Link href={href} onClick={handleClick} {...rest}>
      {children}
    </Link>
  );
};
